import debounce from 'lodash.debounce';
import React, { Fragment, useEffect, useState } from 'react';

import useIntersect from '../../../hooks/useIntersect';
import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { legendItems } from '../../../utils/competition-legends';
import { isNonLinearCompetition } from '../../../utils/match-status';
import { makeClassNames } from '../../../utils/styles';
import ErrorComponent from '../../atoms/error/error';
import { IGliderItem } from '../../atoms/glider-item/glider-item';
import ResultsTitle from '../../atoms/results-title/results-title';
import Spacer from '../../atoms/spacer/spacer';
import Legend from '../../molecules/legend/legend';
import ResultsTable, { IResultsTable } from '../../organisms/results-table/results-table';
import ErrorBoundary from '../error-boundary/error-boundary';
import Glider from '../../organisms/glider/glider';
import './competition-calendar-results.scss';

export interface ICompetitionCalendarResultsTable extends IResultsTable {
  round: number;
  competitionPhase?: string | null;
  competitionPhaseRound?: string | null;
}

export interface ICompetitionCalendarResults extends IComponent {
  competitionMatchList: ICompetitionCalendarResultsTable[];
  competitionMatchListMenu?: IMatchMenu;
}

interface IMatchMenu {
  gliderItems: IGliderItem[];
  activeIndex: number;
}

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidCompetitionMatchList = (matchList: ICompetitionCalendarResultsTable[]) =>
  Boolean(matchList && matchList.length);

const createId = (i: number): string => {
  return `${t('href.anchor.prefix.id')}-${i}`;
};

const getResultTableTitle = (resultTableModel: ICompetitionCalendarResultsTable): string =>
  resultTableModel && resultTableModel.competitionPhase
    ? `${resultTableModel.competitionPhase}`
    : `${t('competition.calendar.results.table.title')} ${resultTableModel.round}`;

const classNames = () =>
  makeClassNames({
    competitionCalendarResults: 'competition-calendar-results',
  });

const setPageHash = (params: { activeHash: string }): void => {
  const { activeHash } = params;
  window.location.hash = activeHash;
};

/**
 * COMPETITION CALENDAR COMPONENT
 */
const CompetitionCalendarResults = (props: ICompetitionCalendarResults) => {
  const { competitionMatchList, competitionMatchListMenu, trackingData } = props;

  const cn = classNames();
  const firstIndexActive = competitionMatchListMenu?.activeIndex;

  const [activeIndex, setActiveIndex] = useState(firstIndexActive);
  const [missingRounds, setMissingRounds] = useState<number[]>([]);

  const onIntersect = debounce(
    (item: HTMLElement) => {
      setActiveIndex(Number(item.dataset.index));
    },
    500,
    { leading: true }
  );

  useIntersect('[data-intersect="true"]', onIntersect);

  /** Effect to change the activeIndex from new competitionMatchListMenu model */
  useEffect(() => {
    let activeHash: string | undefined;

    if (typeof competitionMatchListMenu?.activeIndex === 'number') {
      window.location.hash = '';

      activeHash = `giornata-${competitionMatchListMenu.activeIndex}`;
      setActiveIndex(competitionMatchListMenu?.activeIndex);
    }

    if (activeHash) {
      setPageHash({ activeHash });
    }
  }, [competitionMatchListMenu?.activeIndex, competitionMatchListMenu?.gliderItems]);

  /** Render LEGEND only after COMPONENT is rendered */
  const [showLegend, setShowLegend] = useState(false);
  useEffect(() => {
    setShowLegend(isNonLinearCompetition(competitionMatchList));
  }, [competitionMatchList]);

  useEffect(() => {
    const roundSequence = competitionMatchList.map((match) => match.round);
    const result: number[] = [];

    for (let i = 1; i <= Math.max(...roundSequence); i++) {
      if (roundSequence.indexOf(i) === -1) {
        result.push(i);
      }
    }

    setMissingRounds(result);
  }, [competitionMatchList]);

  return (
    <div className={cn.competitionCalendarResults}>
      <ErrorBoundary hasError={!isValidCompetitionMatchList(competitionMatchList)} errorComponent={getErrorComponent()}>
        <Fragment>
          {competitionMatchListMenu && (
            <Glider
              activeIndex={activeIndex}
              items={competitionMatchListMenu?.gliderItems}
              onSelectedGliderItem={setActiveIndex}
            />
          )}
          <Spacer size={30} />
          {competitionMatchList.map((resultTableModel: ICompetitionCalendarResultsTable, index: number) => {
            const resultTableTitle = getResultTableTitle(resultTableModel);
            const id = createId(resultTableModel.round);

            return (
              <Fragment key={id}>
                {missingRounds.includes(resultTableModel.round - 1) && (
                  <div id={createId(resultTableModel.round - 1)} data-intersect="false" data-index={index} />
                )}
                <div id={id} data-intersect="true" data-index={index}>
                  {resultTableModel && resultTableModel.round && (
                    <ResultsTitle title={resultTableTitle} selfUpdate={index === 0} />
                  )}
                  <ResultsTable
                    type="results-thick"
                    matchDayList={resultTableModel.matchDayList}
                    trackingData={trackingData}
                    showMatchRowLiveResult={false}
                  />
                </div>
              </Fragment>
            );
          })}
          {showLegend && <Legend items={legendItems} />}
        </Fragment>
      </ErrorBoundary>
    </div>
  );
};

export default CompetitionCalendarResults;
